import * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { FilterList } from "@mui/icons-material";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type {
  FilterField,
  FilterValue,
  FilterableAttribute,
  DynamicFilter,
} from "../types";
import { FilterOperator } from "../types";
import {
  getDefaultOperatorForDataType,
  getOperatorsForDataType,
} from "../utils";
import { FilterRow, DynamicFilterRow } from ".";

export interface FilterButtonProps {
  filterFields?: FilterField[];
  filters?: FilterValue[];
  onFiltersChange?: (filters: FilterValue[]) => void;

  filterableAttributes?: FilterableAttribute[];
  dynamicFilters?: DynamicFilter[];
  onAddFilter?: () => void;
  onUpdateFilter?: (index: number, updates: Partial<DynamicFilter>) => void;
  onRemoveFilter?: (index: number) => void;
  onClearFilters?: () => void;
  onForeignKeySearch?: (
    referenceTable: string,
    search: string,
    callback: (options: { label: string; value: string | number }[]) => void,
  ) => void;
  className?: string;
}

export function FilterButton({
  filterFields = [],
  filters = [],
  onFiltersChange,
  filterableAttributes = [],
  dynamicFilters = [],
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  onForeignKeySearch,
  className,
}: FilterButtonProps) {
  const [open, setOpen] = React.useState(false);

  const useDynamic = filterableAttributes.length > 0 && !!onAddFilter;

  const activeCount = useDynamic
    ? dynamicFilters.filter(
        (f) => f.filterBy && f.value !== null && f.value !== "",
      ).length
    : filters.filter((f) => f.field && f.value).length;

  const getAttributeByField = (field: string) =>
    filterableAttributes.find((attr) => attr.field === field);

  const handleAddFilter = () => {
    onFiltersChange?.([
      ...filters,
      { field: "", operator: "contains", value: "" },
    ]);
  };

  const handleUpdateFilter = (index: number, filter: FilterValue) => {
    const newFilters = [...filters];
    newFilters[index] = filter;
    onFiltersChange?.(newFilters);
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    onFiltersChange?.(newFilters);
  };

  // Hide entirely if neither legacy nor dynamic config provided
  const showButton = useDynamic
    ? filterableAttributes.length > 0
    : filterFields.length > 0;
  if (!showButton) return null;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          intent="primary"
          design="outline"
          size="iconMd"
          className={cn(
            "aero:h-10 aero:rounded aero:shrink-0 aero:mt-7 aero:relative",
            "aero:hover:bg-brand-primary-50 ",
            activeCount > 0
              ? "aero:w-auto aero:px-3 aero:gap-2 aero:bg-brand-primary-50 aero:border-brand-primary-800"
              : "aero:w-11",
            className,
          )}
        >
          <FilterList className="aero:h-5 aero:w-5" />
          {activeCount > 0 && (
            <span className="aero:flex aero:bg-brand-primary-700 aero:items-center aero:justify-center aero:h-5 aero:w-5 aero:rounded-full aero:text-white aero:text-xs aero:font-bold">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="aero:w-[600px] aero:max-w-[90vw] aero:p-5 aero:rounded-lg aero:shadow-[0px_4px_4px_0px_rgba(127,127,127,0.1)]"
        sideOffset={8}
      >
        <div className="aero:flex aero:flex-col aero:gap-3 aero:min-w-0">
          <div className="aero:flex aero:items-center aero:justify-between aero:flex-wrap aero:gap-2">
            <h4 className="aero:text-sm aero:font-bold aero:text-foreground">
              Filter by
            </h4>
            <div className="aero:flex aero:items-center aero:gap-2 aero:flex-shrink-0">
              {useDynamic ? (
                <Button
                  type="button"
                  intent="secondary"
                  design="outline"
                  size="sm"
                  onClick={() => {
                    onAddFilter?.();
                  }}
                >
                  <Plus className="aero:h-4 aero:w-4" />
                  Add Filter
                </Button>
              ) : (
                <Button
                  type="button"
                  intent="secondary"
                  design="outline"
                  size="sm"
                  onClick={handleAddFilter}
                >
                  <Plus className="aero:h-4 aero:w-4" />
                  Add Filter
                </Button>
              )}
            </div>
          </div>

          {useDynamic ? (
            dynamicFilters.length === 0 ? (
              <p className="aero:text-sm aero:text-neutral-500 aero:text-center aero:py-4">
                No filters added. Click "Add Filter" to add one.
              </p>
            ) : (
              <>
                <div className="aero:flex aero:flex-col aero:gap-2">
                  {dynamicFilters.map((filter, index) => (
                    <DynamicFilterRow
                      key={index}
                      filter={filter}
                      attribute={getAttributeByField(filter.filterBy)}
                      availableAttributes={filterableAttributes}
                      onUpdate={(updates) => onUpdateFilter?.(index, updates)}
                      onRemove={() => onRemoveFilter?.(index)}
                      onForeignKeySearch={onForeignKeySearch}
                      onChangeAttribute={(newAttr) => {
                        const defaultOperator = getDefaultOperatorForDataType(
                          newAttr.dataType,
                        );
                        const operatorLabels = getOperatorsForDataType(
                          newAttr.dataType,
                        );

                        const operatorLabel =
                          operatorLabels
                            .find((op) => op.value === defaultOperator)
                            ?.label.toLowerCase() || "contains";
                        const defaultValue =
                          defaultOperator === FilterOperator.RANGE
                            ? { from: "", to: "" }
                            : "";
                        onUpdateFilter?.(index, {
                          filterBy: newAttr.field,
                          type: operatorLabel,
                          value: defaultValue,
                        });
                      }}
                    />
                  ))}
                </div>
                {onClearFilters && (
                  <div className="aero:flex aero:items-start">
                    <Button
                      type="button"
                      intent="destructive"
                      design="text"
                      size="sm"
                      onClick={onClearFilters}
                    >
                      <Trash2 className="aero:h-4 aero:w-4" />
                      Clear all
                    </Button>
                  </div>
                )}
              </>
            )
          ) : filters.length === 0 ? (
            <p className="aero:text-sm aero:text-neutral-500 aero:text-center aero:py-4">
              No filters added. Click "Add Filter" to add one.
            </p>
          ) : (
            <div className="aero:flex aero:flex-col aero:gap-2">
              {filters.map((filter, index) => (
                <FilterRow
                  key={index}
                  filter={filter}
                  fields={filterFields}
                  onUpdate={(f) => handleUpdateFilter(index, f)}
                  onRemove={() => handleRemoveFilter(index)}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
